import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Button, Stack, Text } from '@/design-system'
import { Logo } from '@/app/Logo'
import { cn } from '@/lib/cn'
import { BRAND } from '@/brand/brand'
import { resetDraft } from './progress'
import styles from './Onboarding.module.css'

const SLIDES = [
  {
    title: 'Four accounts and a card, in one app.',
    body: 'Dollars, euros, pesos and reais with local details in your name, a Mastercard debit card and a crypto wallet.',
    cta: 'Get started',
  },
  {
    title: 'One card, every currency you hold.',
    body: 'Pay from the balance in that currency. Nothing converts, so nothing is marked up.',
    cta: 'Next',
  },
  {
    title: 'Send abroad in minutes, not days.',
    body: 'Send to Mexico, Colombia, Brazil and Bolivia, with the fee and the rate shown before you confirm.',
    cta: 'Create my account',
  },
] as const

const SPLASH_KEY = 'pass.splashSeen'

function shouldSplash(): boolean {
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false
    return window.sessionStorage.getItem(SPLASH_KEY) !== '1'
  } catch {
    return false
  }
}

/** 01 Splash, then 02–04: the three reasons, and the two ways in. */
export function OnboardingWelcome() {
  const navigate = useNavigate()
  const [splash, setSplash] = useState(shouldSplash)
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (!splash) return
    const timer = window.setTimeout(() => {
      try {
        window.sessionStorage.setItem(SPLASH_KEY, '1')
      } catch {
        /* the splash will simply show again */
      }
      setSplash(false)
    }, 1100)
    return () => window.clearTimeout(timer)
  }, [splash])

  if (splash) {
    return (
      <div className={styles.splash} role="status" aria-label="Loading">
        <div className={styles.splashInner}>
          <Logo size={34} tone="dark" />
          <Text variant="eyebrow" tone="onDarkMuted">
            Accounts · Cards · Wallet · Travel
          </Text>
        </div>
      </div>
    )
  }

  const slide = SLIDES[index] ?? SLIDES[0]
  const last = index === SLIDES.length - 1

  const start = () => {
    resetDraft()
    navigate('/onboarding/create')
  }

  return (
    <div className={styles.welcome}>
      <Link to="/onboarding/create" className={styles.skip} onClick={() => resetDraft()}>
        <Text variant="caption" weight="semibold" tone="secondary">
          Skip
        </Text>
      </Link>

      <div className={styles.slide} aria-live="polite">
        <Text as="h1" weight="bold" className={styles.slideTitle}>
          {slide.title}
        </Text>
        <Text variant="body" tone="secondary">
          {slide.body}
        </Text>
      </div>

      <Stack gap={6}>
        <div className={styles.dots} role="tablist" aria-label="Why people use Pass">
          {SLIDES.map((item, i) => (
            <button
              key={item.title}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Slide ${i + 1} of ${SLIDES.length}`}
              className={cn(styles.dot, i === index && styles.dotOn)}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>

        <div className={styles.actions}>
          <Button size="lg" block iconEnd="arrow_forward" onClick={last ? start : () => setIndex(index + 1)}>
            {slide.cta}
          </Button>
          <Button variant="ghost" size="md" block to="/login">
            I already have an account
          </Button>
        </div>

        <Text variant="caption" tone="secondary">
          {BRAND.name} is a financial technology company, not a bank. Accounts are held with regulated
          partners.
        </Text>
      </Stack>
    </div>
  )
}
